import React from "react";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Stack,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import UploadProgress from "../../components/UploadProgress";
import { uploadToS3 } from "../../utils/s3-upload";
import { isEmpty } from "../../utils/utils";

// Build binaries this portal distributes. Kept in sync with the backend's
// ALLOWED_EXTENSIONS (app/config.py) minus the image types, which this form never
// uploads.
const ACCEPTED_APP_EXTENSIONS = [".apk", ".aab", ".ipa", ".zip"];

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Form = ({
  projectId = "",
  projects = [],
  variants = [],
  variantsLoading = false,
  canManageVariants = false,
  onManageVariants,
  item = undefined,
  addVersionMode = false,
  lockedBuild = undefined,
  replaceKeys = [],
  /** Pre-fills the drop zone, e.g. from a page-level drag-and-drop capture. */
  initialFile = undefined,
  onConfirm,
  onClose,
  onProgress,
  /** Called after an S3 upload failure, e.g. so a host can reset its progress bar. */
  onUploadFailed = undefined,
}) => {
  const [data, setData] = React.useState(
    item ||
      (addVersionMode && lockedBuild
        ? {
            projectId,
            ...lockedBuild,
            isLatestVersion: true,
            isAvailableForDownload: false,
          }
        : {
            projectId,
            isAvailableForDownload: false,
            isLatestVersion: false,
            ...(initialFile ? { file: initialFile } : {}),
          }),
  );
  const [fileName, setFileName] = React.useState(
    item?.file?.name || initialFile?.name || undefined,
  );
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState();
  const [uploadFailed, setUploadFailed] = React.useState(false);
  const [progress, setProgress] = React.useState(null);

  const uploadFile = () => {
    uploadToS3({
      file: data.file,
      onSuccess: (response) => {
        setLoading(false);
        onConfirm({
          ...data,
          ...response,
        });
      },
      onFailure: (error) => {
        setLoading(false);
        setProgress(null);
        setError(error || "An error occurred while uploading the file.");
        setUploadFailed(true);
        if (onUploadFailed) onUploadFailed();
      },
      onProgress: (percent) => {
        setProgress(percent);
        if (onProgress) onProgress(percent);
      },
    });
  };

  const onConfirmClick = () => {
    setUploadFailed(false);
    const hasRequiredFields =
      "projectId" in data &&
      "versionName" in data &&
      "versionCode" in data &&
      "releaseNotes" in data;

    if (!hasRequiredFields) {
      setError("Please fill in all required fields.");
      return;
    }

    if (item) {
      // Edits go through the update endpoint, which neither requires nor accepts
      // a variant change — a build's variant is set once at create.
      onConfirm(data);
      return;
    }

    // Validated here so a missing variant never becomes a server round trip
    // that comes back as "400 Missing params: variantId".
    if (!data.variantId) {
      setError("Please select a variant for this build.");
      return;
    }

    if (!("file" in data)) {
      setError("Please upload a file.");
      return;
    }

    setLoading(true);
    uploadFile();
  };

  // The server identifies a build by variant + version name + version code, and
  // silently overwrites a match — replacing the record and deleting the old
  // file. Detect the clash here so the user is warned before the upload starts.
  const collisionKey = `${data?.variantId ?? ""}__${data?.versionName ?? ""}__${
    data?.versionCode ?? ""
  }`;
  const willReplaceBuild =
    !item &&
    Boolean(data?.variantId && data?.versionName && data?.versionCode) &&
    replaceKeys.includes(collisionKey);

  const selectedVariant = variants.find(
    (variant) => String(variant.id) === String(data?.variantId),
  );
  const hasNoVariants = !variantsLoading && variants.length === 0;

  // The backend fixes a build's variant at create time, so once one is assigned
  // it can't be changed here. But a build with no variant at all — a legacy
  // upload from before variants existed, or one made while the project had none
  // — must stay editable, or there is never a way to assign it one.
  const variantLocked = Boolean(item) && Boolean(item?.variantId);

  // The app name field was removed from this form — a fresh upload takes its
  // name from the selected variant instead of asking the user to type one.
  // Edits and add-version uploads keep whatever name the record already has.
  React.useEffect(() => {
    if (item || addVersionMode) return;
    if (selectedVariant) {
      setData((prev) => ({ ...prev, name: selectedVariant.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant?.id, item, addVersionMode]);

  const handleChange = (event) => {
    if (event.target.name === "file") {
      const files = event.target.files;
      const file = files[0];
      setFileName(file.name);
      setData({ ...data, [event.target.name]: file });
    } else {
      setData({ ...data, [event.target.name]: event.target.value });
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError()}>
          {error}
        </Alert>
      )}
      {addVersionMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Adding a new version of this build — provide the updated file and
          release notes. The app name is inherited. Give it a new version name or
          code, or it will replace the existing build.
        </Alert>
      )}
      {hasNoVariants && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            canManageVariants && onManageVariants ? (
              <Button color="inherit" size="small" onClick={onManageVariants}>
                Create Variant
              </Button>
            ) : undefined
          }
        >
          This project has no variants yet. Every build must be linked to one
          {canManageVariants
            ? `, so create a variant before ${item ? "this can be saved" : "uploading"}.`
            : ` — ask a product manager to create one before ${item ? "this can be saved" : "uploading"}.`}
        </Alert>
      )}
      {!hasNoVariants && item && !variantLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This build has no variant assigned yet — select one below and save to
          fix it.
        </Alert>
      )}
      {willReplaceBuild && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>{selectedVariant?.name}</b> already has a build with version{" "}
          {data?.versionName} ({data?.versionCode}). Uploading will{" "}
          <b>replace that build and delete its file</b>. Change the version name
          or code to add a separate build.
        </Alert>
      )}
      <FormControl fullWidth sx={{ mb: 2 }} required>
        <InputLabel id="app-project-label">Project</InputLabel>
        <Select
          labelId="app-project-label"
          id="projectId"
          name="projectId"
          label="Project"
          value={projectId}
          onChange={handleChange}
          disabled={addVersionMode}
        >
          {projects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>Select a project for the upload</FormHelperText>
      </FormControl>

      {/* Required: the upload endpoint rejects a build with no variantId, and a
          build's identity is variant + version name + version code. */}
      <FormControl fullWidth sx={{ mb: 2 }} required>
        <InputLabel id="app-variant-label" shrink>Variant</InputLabel>
        <Select
          labelId="app-variant-label"
          id="variantId"
          name="variantId"
          label="Variant"
          value={
            variants.some((v) => String(v.id) === String(data?.variantId))
              ? data.variantId
              : ""
          }
          onChange={handleChange}
          disabled={variantLocked || variantsLoading || hasNoVariants}
          displayEmpty
          notched
        >
          <MenuItem value="" disabled>
            {variantsLoading ? "Loading variants…" : "Select a variant"}
          </MenuItem>
          {variants.map((variant) => (
            <MenuItem key={variant.id} value={variant.id}>
              {variant.name}
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
                sx={{ ml: 1, fontFamily: "monospace" }}
              >
                {variant.package_name}
              </Typography>
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {selectedVariant
            ? `Uploading to ${selectedVariant.package_name}`
            : "Select the variant this build belongs to"}
        </FormHelperText>
      </FormControl>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          id="version-name"
          name="versionName"
          label="Version Name"
          placeholder="1.4.0"
          value={data?.versionName}
          onChange={handleChange}
          size="small"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          id="version-code"
          name="versionCode"
          label="Version Code"
          placeholder="210"
          value={data?.versionCode}
          onChange={handleChange}
          size="small"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <TextField
        label="Release Notes"
        name="releaseNotes"
        placeholder="What changed in this release?"
        value={data?.releaseNotes ?? ""}
        onChange={handleChange}
        size="small"
        fullWidth
        multiline
        minRows={3}
        required
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />

      {isEmpty(item) && (
        <Box sx={{ my: 2 }}>
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file)
                handleChange({ target: { name: "file", files: [file] } });
            }}
            sx={{
              borderWidth: 2,
              borderStyle: dragOver ? "solid" : "dashed",
              borderColor: dragOver ? "primary.main" : "text.disabled",
              borderRadius: 2,
              backgroundColor: (t) =>
                dragOver ? alpha(t.palette.primary.main, 0.05) : "transparent",
              p: 3,
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 40,
                color: dragOver ? "primary.main" : "text.disabled",
                mb: 1,
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Drag & drop your APK, AAB, IPA, or ZIP here, or{" "}
              <Box
                component="span"
                sx={{ color: "primary.main", textDecoration: "underline" }}
              >
                click to browse
              </Box>
            </Typography>
            <VisuallyHiddenInput
              ref={fileInputRef}
              name="file"
              type="file"
              accept={ACCEPTED_APP_EXTENSIONS.join(",")}
              onChange={handleChange}
            />
          </Box>
          {fileName && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block", textAlign: "center" }}
            >
              {fileName}
            </Typography>
          )}
        </Box>
      )}

      <FormGroup>
        {/* Controlled, not defaultChecked: the checkbox's visual state must always
            match `data`, or an untouched checkbox silently omits the field from the
            submitted payload — the upload then can't be found by the downloads
            query, which only matches isAvailableForDownload === true. */}
        <FormControlLabel
          name="isAvailableForDownload"
          control={<Checkbox checked={Boolean(data?.isAvailableForDownload)} />}
          onChange={(e) =>
            handleChange({
              target: { name: e.target.name, value: e.target.checked },
            })
          }
          label="Available for download?"
        />
        <FormControlLabel
          name="isLatestVersion"
          control={<Checkbox checked={Boolean(data?.isLatestVersion)} />}
          onChange={(e) =>
            handleChange({
              target: { name: e.target.name, value: e.target.checked },
            })
          }
          label="Set as latest version?"
        />
      </FormGroup>

      {progress !== null && (
        <UploadProgress phase="uploading" progress={progress} />
      )}

      <Stack
        spacing={3}
        direction="row"
        sx={{ mt: 2, justifyContent: "flex-end" }}
      >
        <Button
          variant="contained"
          color={willReplaceBuild ? "error" : "primary"}
          loading={loading}
          disabled={hasNoVariants && !item}
          onClick={onConfirmClick}
        >
          {willReplaceBuild
            ? "Replace Existing Build"
            : uploadFailed
              ? "Retry"
              : "Confirm"}
        </Button>

        <Button variant="outlined" color="danger" onClick={onClose}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default Form;
